import { AccessType } from "../types/enums/AccessType";
import { IconName } from "../components-v2/iconMapping";
import { User } from "../types/UserInfo";
import { getFullName } from "../utils/Roles";
import { adminLinks, boardMemberLinks, HeaderLink, shareholderLinks, SubLink } from "./headerLinks";
import { CompanyItem, Portfolio, SignDocumentCount, SigningMethod } from "../common/common";
import { Language } from "../i18n/languages";
import {
  extractPortfolioBaseUrl,
  extractPortfolioCompaniesUrl,
  extractPortfolioContactsUrl,
  extractPortfolioFollowUpCompanyInfoUrl,
  extractPortfolioFollowUpMattersUrl,
  extractPortfolioFollowUpSignaturesUrl,
  PortfolioHeader,
  SelectedPortfolioOption,
} from "../portfolio/portfolioDomain";
import { TMessage } from "../i18n/tt";
import { UserPrefs } from "../stores/userPrefsDomain";
import { PaidSubscription } from "../common/subscription";
import { CustomRoleForUser } from "../common/customRole";
import { stripQueryString } from "../utils/queryParams";
import { DCEvent } from "../events/eventsDomain";
import { ReactNode } from "react";
import { ButtonProps } from "../components-v2/Button";

export type AppBarActions = {
  addAdministrator?: (companyItem: CompanyItem) => void;
  changeLanguage?: (language: Language) => void;
  logOut?: () => void;
  navigateToContactUs?: () => void;
  navigateToPortfolio?: () => void;
  navigateToPrices?: () => void;
  navigateToUserSettings?: () => void;
  navigateToFeedback?: () => void;
  openCompanySettings?: () => void;
  openPaymentDialog?: DCEvent[];
  openHelpCenter?: () => void;
  openCookieSettings?: () => void;
  selectCompany?: (company: {
    organisationNumber: string;
    accessType: AccessType;
    shareholderId?: number;
  }) => void;
  upgradeToPro?: () => void;
  filterCompaniesInPortfolio?: (type: SelectedPortfolioOption) => void;
  openCreatePortfolioDialog?: () => void;
  openPortfolio?: ({ id, name, createdAt, createdBy }: Portfolio) => void;
  openPortfolioSettings?: () => void;
  openBatchSigningPage?: (signingMethod: SigningMethod) => void;
};

export type AppBarSubLink = {
  url: string;
  exact?: boolean;
  text: TMessage;
  isActive: boolean;
  tag?: SubLink["tag"];
  badge?: SubLink["badge"];
  badgeColor?: SubLink["badgeColor"];
};

export type AppBarLink = {
  url: string;
  exact?: boolean;
  text: TMessage;
  icon: IconName;
  isActive: boolean;
  subLinks?: AppBarSubLink[];
  tag?: TMessage;
  badge?: HeaderLink["badge"];
  badgeColor?: HeaderLink["badgeColor"];
};

export type CompanyOption = {
  organisationNumber: string;
  name: string;
  shareholderId?: number;
  accessType: AccessType;
};

export type AppBarData = {
  languages: { language: Language; isSelected?: boolean }[];
  user: {
    name: string;
    company?: string;
    initials: string;
    isImpersonated?: boolean;
    email?: string;
  };
  userMainAction?: ReactNode | [name: string, button: ButtonProps][];
  portfolioUrl: string;
  backToPortfolioTooltip?: TMessage;
  links?: AppBarLink[];
  subLinks?: AppBarSubLink[];
  helpCenter?: AppBarLink;
  companies?: CompanyOption[];
  portfolios?: Portfolio[];
  portfolioHeader?: PortfolioHeader;
  selectedCompany?: CompanyOption;
  selectedPortfolioOption?: SelectedPortfolioOption;
  subscription?: PaidSubscription;
  signDocumentCounts?: SignDocumentCount[];
  actions: AppBarActions;
  displayUserName?: boolean;
};

export function getTotalSignDocumentCount(signDocumentCounts?: SignDocumentCount[]) {
  return signDocumentCounts ? signDocumentCounts.reduce((acc, { count }) => acc + count, 0) : 0;
}

export function groupBySigningMethod(
  signDocumentCounts?: SignDocumentCount[],
): { signingMethod: SigningMethod; count: number }[] {
  if (!signDocumentCounts) return [];
  const grouped = signDocumentCounts.reduce(
    (acc, item) => {
      acc[item.signingMethod] = (acc[item.signingMethod] || 0) + item.count;
      return acc;
    },
    {} as { [key in SigningMethod]: number },
  );

  return Object.entries(grouped).map(([signingMethod, count]) => ({
    signingMethod: signingMethod as SigningMethod,
    count,
  }));
}

export function prepLanguages(current: Language): AppBarData["languages"] {
  return Object.values(Language).map((l) => ({
    language: l,
    isSelected: l === current,
  }));
}

export function prepUser(user: User): AppBarData["user"] {
  return {
    initials: (user.firstName?.slice(0, 1) ?? "") + user.lastName.slice(0, 1).toLocaleUpperCase(),
    name: getFullName(user),
    isImpersonated: user.isImpersonated,
  };
}

export function getMenuLinks({
  accessType,
  orgNumber,
  isMobile,
  portfolioUrl,
  shareholderId,
  country,
  subscription,
  userRole,
  roleId,
  followUpCount,
}: {
  orgNumber: string;
  accessType: AccessType | null;
  isMobile: boolean;
  portfolioUrl: string;
  shareholderId?: number;
  country?: string;
  subscription?: PaidSubscription;
  userRole?: CustomRoleForUser;
  roleId?: string;
  followUpCount?: number;
}): HeaderLink[] {
  const portfolio: HeaderLink[] = isMobile
    ? [
        {
          to: portfolioUrl,
          basePath: portfolioUrl,
          text: { key: "header.portfolio" },
          exact: true,
          icon: "briefcase",
        },
      ]
    : [];
  if (
    accessType !== AccessType.BOARD_MEMBER &&
    accessType !== AccessType.SHAREHOLDER &&
    accessType !== AccessType.ADDITIONAL_REPRESENTATIVE &&
    userRole !== undefined &&
    userRole.customRoleId !== "all"
  ) {
    return portfolio.concat(
      adminLinks({ orgNo: orgNumber, country, subscription, userRole, roleId }),
    );
  } else if (accessType === AccessType.ADMIN) {
    return portfolio.concat(
      adminLinks({ orgNo: orgNumber, country, subscription, roleId, followUpCount }),
    );
  } else if (accessType === AccessType.BOARD_MEMBER) {
    return portfolio.concat(boardMemberLinks(orgNumber));
  } else if (
    (accessType === AccessType.SHAREHOLDER ||
      accessType === AccessType.ADDITIONAL_REPRESENTATIVE) &&
    shareholderId
  ) {
    return portfolio.concat(shareholderLinks(orgNumber, shareholderId));
  }
  return [];
}

function isActive({ to, exact }: { to: string; exact?: boolean }, currentUrl: string) {
  const actualTo = stripQueryString(to);
  if (exact) {
    return actualTo === currentUrl;
  }
  return currentUrl.includes(actualTo);
}

export function prepMenuLinks(params: {
  orgNumber: string;
  accessType: AccessType | null;
  currentUrl: string;
  isMobile: boolean;
  portfolioUrl: string;
  shareholderId?: number;
  country?: string;
  subscription?: PaidSubscription;
  userPrefs: UserPrefs;
  userRole?: CustomRoleForUser;
  roleId?: string;
  followUpCount?: number;
}): AppBarLink[] {
  const { currentUrl } = params;
  return getMenuLinks(params).map((l) => {
    const subLinks = l.subLinks?.map<AppBarSubLink>((sl) => ({
      url: sl.to,
      exact: sl.exact,
      text: sl.text,
      badge: sl.badge,
      badgeColor: sl.badgeColor,
      isActive: isActive(sl, currentUrl),
      tag: sl.tag,
    }));
    return {
      url: l.to,
      exact: l.exact,
      icon: l.icon,
      text: l.text,
      badge: l.badge,
      badgeColor: l.badgeColor,
      isActive: isActive(l, currentUrl) || subLinks?.some((sl) => sl.isActive) || false,
      subLinks,
    };
  });
}

export function prepFollowUpsLink({
  currentUrl,
  portfolio,
  portfolioFollowUpCount,
}: {
  currentUrl: string;
  portfolio: SelectedPortfolioOption;
  portfolioFollowUpCount: number;
}): AppBarLink {
  const signaturesUrl = extractPortfolioFollowUpSignaturesUrl(portfolio);
  const mattersUrl = extractPortfolioFollowUpMattersUrl(portfolio);
  const companyInfoUrl = extractPortfolioFollowUpCompanyInfoUrl(portfolio);
  return {
    url: signaturesUrl,
    exact: true,
    icon: "clipboard-text",
    text: { key: "header.follow-up" },
    isActive:
      isActive({ to: mattersUrl, exact: false }, currentUrl) ||
      isActive({ to: signaturesUrl, exact: false }, currentUrl) ||
      isActive({ to: companyInfoUrl, exact: false }, currentUrl),
    tag: portfolioFollowUpCount > 0 ? { translated: String(portfolioFollowUpCount) } : undefined,
  };
}

export function prepContactsLink({
  currentUrl,
  portfolio,
}: {
  currentUrl: string;
  portfolio: SelectedPortfolioOption;
}): AppBarLink {
  const url = extractPortfolioContactsUrl(portfolio);
  return {
    url,
    exact: true,
    icon: "user-list",
    text: { key: "header.contacts" },
    isActive: isActive({ to: "/contacts", exact: false }, currentUrl),
  };
}

export function prepHelpCenterLink({
  currentUrl,
  icon,
  portfolio,
}: {
  currentUrl: string;
  icon?: IconName;
  portfolio?: SelectedPortfolioOption;
}): AppBarLink {
  const baseUrl = portfolio ? extractPortfolioBaseUrl(portfolio) : "";
  return {
    url: `${baseUrl}/help-center`,
    icon: icon ?? "rocket",
    text: { key: "header.help-center" },
    isActive: isActive({ to: `${baseUrl}/help-center` }, currentUrl),
  };
}

export function prepPortfolioCompaniesLink({
  currentUrl,
  portfolio,
}: {
  currentUrl: string;
  portfolio: SelectedPortfolioOption;
}): AppBarLink {
  const url = `${extractPortfolioCompaniesUrl(portfolio)}`;
  return {
    url,
    exact: true,
    icon: "buildings",
    text: { key: "header.companies" },
    isActive: isActive({ to: url, exact: false }, currentUrl),
  };
}
